import { useMemo } from 'react';
import { DiscountType, JsonData, JsonDataItem } from 'types';

type DiscountOption = {
  value: string;
  label: string;
  children: { value: string; label: string }[];
  disabled?: boolean;
};

type UseDiscountOptionsType = {
  discountOptions: DiscountOption[];
  getLabel: (discountType: DiscountType, amount: string) => string;
  extraReasonDiscounts: JsonDataItem[];
};

export const useDiscountOptions = (discounts?: JsonData, excludeId: number[] = []): UseDiscountOptionsType => {
  const getLabel = (discountType: DiscountType, amount: string): string => {
    switch (discountType) {
      case DiscountType.Dollar:
        return `$${amount}`;
      case DiscountType.Percent:
        return `${amount}%`;
      case DiscountType.Time:
        return `${amount}hr`;
      default:
        return amount;
    }
  };

  const discountOptions = useMemo(() => {
    if (!discounts) {
      return [];
    }

    const activeCustomerDiscounts = discounts
      .filter(
        (discount) =>
          discount.customer_ID && discount.customer_ID !== -1 && !excludeId.includes(discount.customer_discount_id),
      )
      .map((discount) => ({
        value: discount.customer_discount_id,
        label: `${getLabel(discount.discount_type, discount.amount)} ${discount.reason}`,
      }));

    const groupedDiscounts = discounts.reduce<{ [key: string]: DiscountOption }>((grouped, discount) => {
      if (excludeId?.includes(discount.customer_discount_id)) return grouped;

      if (discount.customer_ID && discount.customer_ID !== -1) return grouped;

      const discountType = discount.discount_type;

      if (!grouped[discountType]) {
        grouped[discountType] = {
          value: discountType,
          label: discountType,
          children: [],
        };
      }

      grouped[discountType].children.push({
        value: discount.customer_discount_id,
        label: `${getLabel(discountType, discount.amount)} ${discount.discount_desc} - ${discount.reason}`,
      });

      return grouped;
    }, {});

    const discountOptions = Object.values(groupedDiscounts);

    discountOptions.unshift({
      label: 'System',
      value: 'system-discounts',
      children: [],
      disabled: true,
    });

    // Add the active customer discounts if available
    if (activeCustomerDiscounts.length > 0) {
      discountOptions.push({
        label: 'Customer',
        value: 'customer-label',
        children: [],
        disabled: true,
      });
      discountOptions.push({
        label: 'Active Customer Discounts',
        value: 'customer-discounts',
        children: activeCustomerDiscounts,
      });
    }

    return discountOptions;
  }, [discounts, excludeId]);

  const extraReasonDiscounts = discounts?.filter(d => d.reason_extra === 1) || [];
    

  return { discountOptions, getLabel, extraReasonDiscounts };
};
